import type { SessionUser } from '@sl/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiError, authApi } from './api.js';

interface AuthValue {
  user: SessionUser | null;
  loading: boolean;
  isAdmin: boolean;
  canWrite: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await authApi.me()).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const user = data ?? null;
  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading: isLoading,
      isAdmin: user?.role === 'ADMIN',
      canWrite: user?.role === 'ADMIN' || user?.role === 'MEMBER',
      signIn: async (email, password) => {
        const res = await authApi.login(email, password);
        queryClient.setQueryData(['me'], res.user);
      },
      signOut: async () => {
        await authApi.logout();
        queryClient.setQueryData(['me'], null);
        await queryClient.invalidateQueries();
      },
    }),
    [user, isLoading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
